import type { ConnectivityObserver, ConnectivityService } from './service.js'

type WebConnectivityEventTarget = {
    addEventListener(type: string, listener: () => void): void
    navigator: Pick<Navigator, 'onLine'>
    removeEventListener(type: string, listener: () => void): void
}

export type WebConnectivityServiceOptions = {
    reportError?: (error: unknown) => void
    window: WebConnectivityEventTarget
}

export class WebConnectivityService implements ConnectivityService {
    readonly #observers = new Set<ConnectivityObserver>()
    readonly #reportError: (error: unknown) => void
    readonly #window: WebConnectivityEventTarget
    #destroyed = false
    #initialized = false
    #online = $state(true)
    #pending = Promise.resolve()
    #observing = false

    public constructor(options: WebConnectivityServiceOptions) {
        this.#reportError =
            options.reportError ??
            ((error) => console.warn('Unable to apply web connectivity state.', error))
        this.#window = options.window
        this.#online = options.window.navigator.onLine
    }

    public get online(): boolean {
        return this.#online
    }

    public register(observer: ConnectivityObserver): () => void {
        if (this.#destroyed) {
            throw new Error('Connectivity service has been destroyed.')
        }

        this.#observers.add(observer)
        return () => this.#observers.delete(observer)
    }

    public async initialize(): Promise<void> {
        if (this.#destroyed) {
            throw new Error('Connectivity service has been destroyed.')
        }

        if (this.#initialized) {
            return
        }

        this.#initialized = true
        this.#startObserving()
        await this.#pending
    }

    public async resume(): Promise<void> {
        this.#assertInitialized()
        this.#startObserving()
        await this.#pending
    }

    public async suspend(): Promise<void> {
        this.#assertInitialized()
        this.#stopObserving()
        await this.#pending
    }

    public async destroy(): Promise<void> {
        if (this.#destroyed) {
            return
        }

        this.#destroyed = true
        this.#stopObserving()
        this.#observers.clear()
        await this.#pending
        this.#initialized = false
    }

    public async flush(): Promise<void> {
        await this.#pending
    }

    readonly #handleConnectivityChange = (): void => {
        this.#applyCurrentState()
    }

    #applyCurrentState(): void {
        if (!this.#observing) {
            return
        }

        const online = this.#window.navigator.onLine
        this.#online = online
        const observers = [...this.#observers]
        this.#pending = this.#pending.then(async () => {
            const results = await Promise.allSettled(
                observers
                    .filter((observer) => this.#observers.has(observer))
                    .map(async (observer) => await observer.setOnline(online))
            )
            for (const result of results) {
                if (result.status === 'rejected') {
                    this.#reportError(result.reason)
                }
            }
        })
    }

    #startObserving(): void {
        if (this.#observing) {
            return
        }

        this.#observing = true
        this.#window.addEventListener('online', this.#handleConnectivityChange)
        this.#window.addEventListener('offline', this.#handleConnectivityChange)
        this.#applyCurrentState()
    }

    #stopObserving(): void {
        if (!this.#observing) {
            return
        }

        this.#observing = false
        this.#window.removeEventListener('online', this.#handleConnectivityChange)
        this.#window.removeEventListener('offline', this.#handleConnectivityChange)
    }

    #assertInitialized(): void {
        if (this.#destroyed || !this.#initialized) {
            throw new Error('Connectivity service is not available.')
        }
    }
}
